import { Eye } from 'lucide-react';
import type { PostDetail } from '@news-cms/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { absolutizeMediaUrls, mediaAbsoluteUrl } from '@/lib/media';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Live form values overlay onto the saved post so unsaved edits
   *  preview correctly — admin shouldn't have to save first. */
  draft: {
    title: string;
    summary: string | null;
    body: string;
    metaTitle: string | null;
    metaDescription: string | null;
  };
  saved: PostDetail;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Approximate preview of how a post will render on the public site.
 * Not pixel-perfect — the public site is a separate codebase we don't
 * control — but it shows the actual body HTML through the same tiptap-
 * content rules the editor uses, with the inline styles emitted by
 * renderHTML so alignment/width/figures all behave.
 *
 * Image URLs go through absolutizeMediaUrls so cross-origin admin →
 * api works (same fix as the editor itself).
 */
export function PostPreviewDialog({ open, onOpenChange, draft, saved }: Props) {
  const title = draft.title.trim() || saved.title || '(ไม่มีหัวข้อ)';
  const summary = draft.summary?.trim() ?? saved.summary ?? '';
  const bodyHtml = absolutizeMediaUrls(draft.body || '');
  const coverUrl = saved.featuredImage?.url ? mediaAbsoluteUrl(saved.featuredImage.url) : null;
  const coverAlt = saved.featuredImage?.altText ?? '';
  const displayDate = saved.publishedAt ?? saved.updatedAt;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-slate-400" />
            พรีวิว
          </DialogTitle>
          <DialogDescription>
            ตัวอย่างการแสดงผลโดยประมาณ — เว็บ public อาจใช้สไตล์ต่างจากนี้เล็กน้อย
          </DialogDescription>
        </DialogHeader>

        <article className="space-y-4 pt-2">
          {coverUrl && (
            <img
              src={coverUrl}
              alt={coverAlt}
              className="aspect-video w-full rounded-md object-cover"
            />
          )}
          <header className="space-y-2">
            <h1 className="text-3xl font-bold leading-tight text-slate-900 dark:text-slate-50">
              {title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              <span>{saved.author.name}</span>
              <span>·</span>
              <span>{formatDate(displayDate)}</span>
              {saved.readingTimeMinutes > 0 && (
                <>
                  <span>·</span>
                  <span>อ่าน ~{saved.readingTimeMinutes} นาที</span>
                </>
              )}
            </div>
          </header>
          {summary && (
            <p className="border-l-2 border-slate-300 pl-3 text-base italic text-slate-600 dark:border-slate-700 dark:text-slate-400">
              {summary}
            </p>
          )}
          <div
            className="tiptap-content text-base leading-relaxed text-slate-800 dark:text-slate-200"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        </article>
      </DialogContent>
    </Dialog>
  );
}
